<script setup lang="ts">
import { ref } from 'vue'

import { useAuth } from '../composables/useAuth'
import BaseDialog from '../ui/components/BaseDialog.vue'
import { useDialog } from '../ui/dialogs'

const props = defineProps<{ initialEmail?: string }>()

const { resetPassword } = useAuth()
const { close } = useDialog()

const email = ref(props.initialEmail ?? '')
const busy  = ref(false)
const sent  = ref(false)

async function onSubmit() {
  if (busy.value) return
  busy.value = true
  try {
    await resetPassword(email.value.trim())
  } catch {
    // Swallowed on purpose. Surfacing "no such user" would turn this into an
    // account-enumeration oracle; the success message is shown either way.
  } finally {
    busy.value = false
    sent.value = true
  }
}
</script>

<template>
  <BaseDialog title="Reset password" size="narrow" @dismiss="close()">
    <template v-if="!sent">
      <p class="hint">Enter your email and we'll send a reset link.</p>
      <form id="forgot-password-form" @submit.prevent="onSubmit">
        <input
          v-model="email"
          type="email"
          autocomplete="username"
          required
          :disabled="busy"
          placeholder="you@example.com"
          class="input"
        />
      </form>
    </template>

    <p v-else>
      If an account exists for that address, a reset link is on its way.
      Check your spam folder if it doesn't arrive shortly.
    </p>

    <template #actions>
      <template v-if="!sent">
        <button type="button" class="btn btn-secondary" :disabled="busy" @click="close()">
          Cancel
        </button>
        <button type="submit" form="forgot-password-form" class="btn btn-primary" :disabled="busy">
          {{ busy ? 'Sending…' : 'Send link' }}
        </button>
      </template>
      <button v-else type="button" class="btn btn-primary" @click="close()">Done</button>
    </template>
  </BaseDialog>
</template>
